import { fetchApi } from './api'
import { SIGNAL_DEFAULT_TAKE } from '../types/signals'
import type {
  SignalAssigneeList, SignalDetail, SignalHandlingResult, SignalList, SignalListQuery, SignalSummary,
} from '../types/signals'

/**
 * The signal store (`ObservatorySignalsController`, `api/observatory/signals`) and the people side of a signal
 * (`ObservatorySignalHandlingController`, same base route).
 *
 * The READS are bare [Authorize], through the shared `fetchApi`, so a failure arrives as an ApiError carrying its
 * status: the pages tell a 503 (the store, or the handling table, is not provisioned) from a 401, a 403 and a 500.
 *
 * The HANDLING calls — acknowledge, assign, release, and the assignee list — are LighthouseAdmin AND pinned to
 * JwtBearer on the server (EtlWriteAuthorization, A4), exactly as clearing a halt is: the shared API key and Windows
 * sign-ins get a 401 in every environment. So they are sent `auth: 'bearer-only'`: with no Entra token they throw
 * BearerRequiredError BEFORE sending (errorKind 'sign-in-required'), and never fall back to the key. The assignee
 * list is a read, pinned anyway because it serves administrators' user ids.
 *
 * There is no resolve and no snooze here, and none belongs here: the detector alone ends a signal.
 */

const BASE = '/api/observatory/signals'

/**
 * The Issues and Critical tiles' numbers, the coverage that says whether to believe them, and the rule catalogue.
 * Answers 200 with `storeProvisioned: false` before the migration, never a 503.
 */
export function getSignalSummary(): Promise<SignalSummary> {
  return fetchApi<SignalSummary>(`${BASE}/summary`)
}

/**
 * The list endpoint's query string, defaults left out.
 *
 * `state=open`, `skip=0` and `take=50` are the controller's own defaults, so asking for them is asking for nothing:
 * they are omitted, and a query that asks only for defaults sends no query string at all. Everything else is sent as
 * given — the controller refuses (400) an invalid filter rather than ignoring it, and the Inbox drops invalid URL
 * values before they reach here (signalFormat.inboxQueryFromSearch).
 */
export function signalListSearch(query: SignalListQuery = {}): string {
  const params = new URLSearchParams()
  if (query.state && query.state !== 'open') params.set('state', query.state)
  if (query.severity) params.set('severity', query.severity)
  if (query.ruleId) params.set('ruleId', query.ruleId)
  if (query.subjectKind) params.set('subjectKind', query.subjectKind)
  if (query.observation) params.set('observation', query.observation)
  if (query.handling) params.set('handling', query.handling)
  if (query.skip != null && query.skip !== 0) params.set('skip', String(query.skip))
  if (query.take != null && query.take !== SIGNAL_DEFAULT_TAKE) params.set('take', String(query.take))
  return params.toString()
}

/** One page of signals: open first, by severity, newest first. */
export function getSignals(query: SignalListQuery = {}): Promise<SignalList> {
  const qs = signalListSearch(query)
  return fetchApi<SignalList>(`${BASE}${qs ? `?${qs}` : ''}`)
}

/** One signal and its transitions. A 404 means no such signal; the id is sent as ONE path segment. */
export function getSignal(id: string): Promise<SignalDetail> {
  return fetchApi<SignalDetail>(`${BASE}/${encodeURIComponent(id)}`)
}

// ---- handling (bearer-only) ---------------------------------------------------------------------------------

/** The three acts. Each names the revision the caller last read, so a stale page cannot overwrite what it never saw. */
export type SignalHandlingRequest =
  | { verb: 'acknowledge'; id: string; expectedRevision: number }
  | { verb: 'assign'; id: string; expectedRevision: number; assigneeUserId: string }
  | { verb: 'release'; id: string; expectedRevision: number }

export type SignalHandlingVerb = SignalHandlingRequest['verb']

function postHandling(id: string, verb: SignalHandlingVerb, body: Record<string, unknown>): Promise<SignalHandlingResult> {
  return fetchApi<SignalHandlingResult>(`${BASE}/${encodeURIComponent(id)}/${verb}`, {
    method: 'POST',
    body: JSON.stringify(body),
    auth: 'bearer-only',
  })
}

/**
 * "I have got this": the caller owns the signal and has acknowledged it — also how a signal is taken over. A repeat
 * answers `changed: false`. It never resolves the signal: it stays open and counted until the detector ends it.
 */
export function acknowledgeSignal(id: string, expectedRevision: number): Promise<SignalHandlingResult> {
  return postHandling(id, 'acknowledge', { expectedRevision })
}

/**
 * Hand the signal to someone, named by the user id the assignee list served — never by a typed name. The server
 * stamps the name THEY presented. Assigning to oneself is a 400 `UseAcknowledge`; an id nobody has seen act is a 400
 * `UnknownAssignee`. The assignee is not notified.
 */
export function assignSignal(id: string, expectedRevision: number, assigneeUserId: string): Promise<SignalHandlingResult> {
  return postHandling(id, 'assign', { expectedRevision, assigneeUserId })
}

/** Let go: nobody has the signal afterwards. Releasing an unowned signal answers `changed: false`. */
export function releaseSignal(id: string, expectedRevision: number): Promise<SignalHandlingResult> {
  return postHandling(id, 'release', { expectedRevision })
}

/** One act, by verb — the mutation's single entry point, so "Take it over" re-sends the same act with a new revision. */
export function handleSignal(request: SignalHandlingRequest): Promise<SignalHandlingResult> {
  switch (request.verb) {
    case 'acknowledge': return acknowledgeSignal(request.id, request.expectedRevision)
    case 'assign': return assignSignal(request.id, request.expectedRevision, request.assigneeUserId)
    case 'release': return releaseSignal(request.id, request.expectedRevision)
  }
}

/**
 * The people a signal can be handed to: everyone the audit trail saw performing an Observatory write in the last
 * `windowDays` days, newest first, the caller excluded. There is no directory (no Graph consent) to search instead.
 */
export function getSignalAssignees(): Promise<SignalAssigneeList> {
  return fetchApi<SignalAssigneeList>(`${BASE}/assignees`, { auth: 'bearer-only' })
}
